import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useParams } from "react-router";
import { Box, Typography, useTheme } from "@mui/material";
import HeaderBar from "../Shared/HeaderBar/HeaderBar.js"
import SubHeaderBar from "./SubHeader/SubHeaderBar.js";
import EntryContainer from "./Containers/EntryContainer/EntryContainer.js";
import ContactsContainer from "./Containers/ContactsContainer/ContactsContainer.js";
import NavigationBar from "../Shared/NavigationBar/NavigationBar.js"
import { fetchChannelData, getLiveStatus, fetchSchema, fetchChannelSchedule, submitChannelData } from "../../utilities/apiRequests.js";
import { getJwtPayload } from "../../utilities/getJwtPayload.js";
import ChannelSchedule from "./Containers/ChannelSchedule/ChannelSchedule.js";
import { getLogoUrl } from "../../utilities/consts.js";

export default function ChannelPage(){

    const { channelID } = useParams();

    let [pageMode, setPageMode] = useState('read')
    let [stateObject, setStateObject] = useState(null)
    let [contacts, setContacts] = useState(null)
    let [logoURLState, setLogoURLState] = useState({
        imageSrc: `${getLogoUrl()}/${channelID}.svg`,
        imageHash: Date.now()
    })
    let [updatedFieldsCountState, setUpdatedFieldsCountState] = useState(0)

    const { getAccessTokenSilently } = useAuth0();
    let navigate = useNavigate()
    let theme = useTheme()


    // fetch schema and channelData from api
    let [apiResponses, setApiResponses] = useState({schema:null, channelData:null, isLive: null, userCanEdit: null})
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let fetchedChannelData = ""
        let fetchedIsLive = ""
        let fetchedSchema = ""
        let fetchedSchedule = null
        let fetchedUserCanEdit = false

        async function fetchApiResponses()  {

            await getAccessTokenSilently({ authorizationParams: { scope: "read:channels" } })
                .then(async (accessToken) => fetchSchema(accessToken))
                .then((schemaResponse) => (fetchedSchema=schemaResponse))
                .catch((error) => {
                    console.error("Error fetching schema:", error);
                    navigate("/stationspage");
                    setError(true);
                })

            await getAccessTokenSilently({ authorizationParams: { scope: "read:channels" } })
                .then((accessToken) => getLiveStatus(accessToken, channelID))
                .then((isLiveResponse) => (fetchedIsLive=isLiveResponse.isLive))
                .catch((error) => {
                    console.error("Error fetching channel data:", error);
                })

            await getAccessTokenSilently({ authorizationParams: { scope: "read:channels" } })
                .then(async (accessToken) => fetchChannelData(accessToken, channelID))
                .then((channelDataResponse) => (fetchedChannelData=channelDataResponse))
                .catch((error) => {
                    console.error("Error fetching channel data:", error);
                    navigate("/stationspage");
                    setError(true);
                })

            await getAccessTokenSilently({ authorizationParams: { scope: "read:channels" } })
                .then(async (accessToken) => fetchChannelSchedule(accessToken, channelID))
                .then((scheduleResponse) => (fetchedSchedule=scheduleResponse))
                .catch((error) => {
                    console.error("Error fetching channel schedule:", error);
                })

            await getAccessTokenSilently()
                .then(accessToken => {
                    const payload = getJwtPayload(accessToken);
                    if (payload.permissions?.includes("write:channels")) fetchedUserCanEdit = true
                })
                .catch(err => {
                    console.error("Failed to fetch JWT payload:", err);
                });
                
            setApiResponses({channelData:fetchedChannelData, isLive:fetchedIsLive, schema:fetchedSchema, channelSchedule:fetchedSchedule, userCanEdit:fetchedUserCanEdit})
            setLoading(false)
        };

        fetchApiResponses()
    }, [getAccessTokenSilently, navigate, channelID]);


    let schema = apiResponses.schema
    let channelData = apiResponses.channelData
    let schedule = apiResponses.channelSchedule

    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
            }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }
 
    if (error) return;


    delete channelData.channel_id //remove uneditable primary key

    let contactsList = channelData.station_contacts //split contacts from rows here 
    if (contactsList === undefined) contactsList = []
    delete channelData.station_contacts
    delete schema.properties.station_contacts
    
    let stationAttributes = Object.keys(schema.properties).sort(); 

    let state = {}

    for(let i = 0; i < stationAttributes.length; i++){
        let stationAttribute = stationAttributes[i]
        let value = channelData[stationAttribute]
        if (value === undefined) value = ""
        state[stationAttribute] = {
            oldValue: value,
            currentValue: undefined,
            currentInput: undefined,
            lockState: true,
            updatedState: false,
            focusState: false,
            validState: true
        }
    }

    if (stateObject === null) setStateObject({...state})
    if (contacts === null) setContacts([...contactsList])


    const handleRevertAllChangesClick = () => {
        if (updatedFieldsCountState < 0) console.log('updatedFieldsCountState < 0 ERROR')
        else if (updatedFieldsCountState === 0) return
        else {
            setStateObject({...state})
            setContacts([...contactsList])
            setPageMode('read')
        }
    }

    async function handleSaveAllChangesClick() {
        if (updatedFieldsCountState < 0) console.log('updatedFieldsCountState < 0 ERROR')
        else if (updatedFieldsCountState === 0) setPageMode('read')
        else if (updatedFieldsCountState > 0){
            const accessToken = await getAccessTokenSilently({ authorizationParams: { scope: "write:channels" } });
            await submitChannelData(accessToken, channelID, stationAttributes, stateObject, contacts)
            window.location.reload()
        }
    }

    const handleContactChange = (changeType, contact) => {

        let updatedContacts = []

        if (changeType === 'add') updatedContacts = handleAddContact(contact)
        else if (changeType === 'edit') updatedContacts = handleEditContact(contact)
        else if (changeType === 'remove') updatedContacts = handleRemoveContact(contact)

        setContacts([...updatedContacts])
    }

    const handleAddContact = (contact) => {
        contacts.push(contact)
        return contacts
    }

    const handleEditContact = (updatedContact) => {
        let updatedContacts = []
        for (const contact of contacts) {
            if (contact.email === updatedContact.email) updatedContacts.push(updatedContact);
            else updatedContacts.push(contact);
        }
        return updatedContacts
    }

    const handleRemoveContact = (removedContact) => {
        let updatedContacts = contacts.filter(contact => contact.email !== removedContact.email)
        return updatedContacts
    }

    function setStateObjectEntryHelper(stationAttribute, updatedEntry){
        stateObject[stationAttribute] = updatedEntry
        setStateObject({...stateObject})
    }

    function setStateObjectEntryFieldHelper(stationAttribute, field, updatedValue){
        let updatedEntry = stateObject[stationAttribute]
        updatedEntry[field] = updatedValue
        setStateObject({...stateObject, stationAttribute: updatedEntry})
    }

    let call_sign = channelData.call_sign
    let market = channelData.market

    return (
        <>
            <HeaderBar></HeaderBar>
                <Box backgroundColor={theme.palette.ChannelPage.background} width={'100%'} paddingBottom={'100px'}>
                    <SubHeaderBar pageMode={pageMode} setPageMode={setPageMode} channelData={channelData} isLive={apiResponses.isLive} logoURLState={logoURLState} userCanEdit={apiResponses.userCanEdit} handleSaveAllChangesClick={handleSaveAllChangesClick} updatedFieldsCountState={updatedFieldsCountState} handleRevertAllChangesClick={handleRevertAllChangesClick}/>

                    {(pageMode === 'read' || pageMode === 'edit') && (
                        <>
                            <EntryContainer pageMode={pageMode} stationAttributes={stationAttributes} stateObject={stateObject} setStateObjectEntryHelper={setStateObjectEntryHelper}
                                                setStateObjectEntryFieldHelper={setStateObjectEntryFieldHelper} 
                                                logoURLState={logoURLState} setLogoURLState={setLogoURLState}
                                                updatedFieldsCountState={updatedFieldsCountState} setUpdatedFieldsCountState={setUpdatedFieldsCountState}
                                                
                                                />
                            <ContactsContainer contacts={contacts} pageMode={pageMode} call_sign={call_sign} market={market} handleContactChange={handleContactChange} />
                        </>
                    )}

                    {(pageMode === 'schedule') && (
                        <ChannelSchedule schedule={schedule}/>
                    )}
                </Box>
            <NavigationBar></NavigationBar>
        </>
    )
}
